import {
  Fragment,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  usePageContext,
} from 'vike-react/usePageContext';
import {
  useWishlists,
  useFriendships,
  useAuthentication,
} from '@dinosave/core/stores';
import AddItemModal from '@/components/Modals/Items/AddItemModal';
import WishlistModal from '@/components/Modals/Wishlists/WishlistModal';
import {
  WISHLIST,
  RECEIVED,
} from '@/constants/namespaces';
import useModal from '@/hooks/useModal';
import Button from '@/ui/components/Button';
import Text from '@/ui/components/Text';
import {
  GearIcon,
  PlusIcon,
  UsersIcon,
  UserCircleIcon,
  PlusCircleIcon,
  ListPlusIcon,
} from '@/ui/icons';

import styles from './Navigation.module.css';

const Navigation = () => {
  // ##########################################################################
  // PARAMS
  // ##########################################################################

  const {
    urlParsed,
  } = usePageContext();
  const {
    pathname,
  } = urlParsed;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserSlug = authenticatedUser?.slug;

  const wishlist = useWishlists(WISHLIST, s => s.wishlist);

  const receivedFriendshipsPagination = useFriendships(RECEIVED, s => s.friendshipsPagination);
  const receivedFriendshipsTotal = receivedFriendshipsPagination?.total || 0;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [actionsVisible, setActionsVisible] = useState(false);

  const addItemModal = useModal();
  const wishlistModal = useModal();

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const isProfilePathname = [
    `/u/${authenticatedUser?.slug}`,
  ].includes(pathname);
  const isSettingsPathname = [
    '/settings',
    '/settings/account',
    '/settings/currency',
    '/settings/notifications',
  ].includes(pathname);
  const isFriendsPathname = [
    '/friends/accepted',
    '/friends/received',
  ].includes(pathname);

  const unseenRequests = !!receivedFriendshipsTotal;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleAdd = () => setActionsVisible(!actionsVisible);

  const handleAddItem = () => {
    addItemModal.toggle();
    setActionsVisible(false);
  };

  const handleAddWishlist = () => {
    wishlistModal.toggle();
    setActionsVisible(false);
  };

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const navigationStyles = classNames({
    [styles.navigation]: true,
    [styles.actionsVisible]: actionsVisible,
  });

  const linkStyles = (isActive: boolean) => classNames({
    [styles.link]: true,
    [styles.active]: isActive,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  if (
    !authenticatedUser
  ) return;

  return (
    <Fragment>
      <AddItemModal
        {...addItemModal}
        wishlist={wishlist}
      />

      <WishlistModal {...wishlistModal} />

      <div className={navigationStyles}>
        <div className={styles.links}>
          <a
            href={`/u/${authenticatedUserSlug}`}
            className={linkStyles(isProfilePathname)}
          >
            <div className={styles.label}>
              <UserCircleIcon />

              <Text
                kind="caption"
                size="small"
                weight="500"
              >
                Profile
              </Text>
            </div>
          </a>

          <a
            href="/friends/accepted"
            className={linkStyles(isFriendsPathname)}
          >
            <div className={styles.label}>
              <UsersIcon />

              {unseenRequests && (
                <div className={styles.indicator} />
              )}

              <Text
                kind="caption"
                size="small"
                weight="500"
              >
                Friends
              </Text>
            </div>

          </a>
          <a
            href="/settings"
            className={linkStyles(isSettingsPathname)}
          >
            <div className={styles.label}>
              <GearIcon />

              <Text
                kind="caption"
                size="small"
                weight="500"
              >
                Settings
              </Text>
            </div>
          </a>
        </div>

        <div className={styles.add}>
          <Button
            kind="link"
            roundness="round"
            onClick={handleAdd}
            centered
          >
            <div className={styles.addIcon}>
              <PlusIcon />
            </div>
          </Button>

          <div className={styles.actions}>
            <div className={styles.button}>
              <Button
                kind="ghost"
                color="secondary"
                roundness="inherit"
                onClick={handleAddItem}
              >
                <div className={styles.buttonIcon}>
                  <PlusCircleIcon />
                </div>

                <div className={styles.buttonText}>
                  <Text
                    kind="control"
                    size="small"
                  >
                    Add an item
                  </Text>
                </div>
              </Button>
            </div>

            <div className={styles.button}>
              <Button
                kind="ghost"
                color="secondary"
                roundness="inherit"
                onClick={handleAddWishlist}
              >
                <div className={styles.buttonIcon}>
                  <ListPlusIcon />
                </div>

                <div className={styles.buttonText}>
                  <Text
                    kind="control"
                    size="small"
                  >
                    New wishlist
                  </Text>
                </div>
              </Button>
            </div>
          </div>
        </div>

        <button
          className={styles.overlay}
          onClick={handleAdd}
        />
      </div>
    </Fragment>
  );
};

export default Navigation;
